import { Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { DashboardTile, DashboardTileTitle } from "./DashboardTiles";
import { SchedulerOption, SchedulerReschedulingVisit } from "./SchedulerScreen";
import { differenceInMinutes, isSameDay, isSameMinute } from "date-fns";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { map } from "lodash";
import { PropsWithChildren } from "react";
import { roundTime15 } from "@caredna-marlin/utils/dates";

type ProviderMap = Record<number, {id: number, firstName: string, lastName: string}>;

type Props = {
  selectedOption: SchedulerOption | null;
  serviceNames: string[];
  reschedulingVisit?: SchedulerReschedulingVisit;
  providersById: ProviderMap;
  timezone: string;
};

const arrayContentEquals = (arr1: Array<any>, arr2: Array<any>) => {
  return JSON.stringify(arr1.sort()) === JSON.stringify(arr2.sort());
};

export default function SchedulerSelectedOptionDetail({
  selectedOption,
  serviceNames,
  reschedulingVisit,
  providersById,
  timezone,
}: Props) {
  const { datef, timeLabelWithZone, timeRangeLabel } = useZonedDateFormat();

  if (!selectedOption) return null;

  const providerIds =  map(selectedOption.resources, "providerId").sort();

  const duration = differenceInMinutes(
    selectedOption.endTime,
    selectedOption.startTime
  );
  const reschedulingVisitDuration =
    reschedulingVisit &&
    differenceInMinutes(reschedulingVisit.endTime, reschedulingVisit.startTime);

  const reschedulingVisitServiceNames = reschedulingVisit
    ? map(map(reschedulingVisit.visitServices, "service"), "name")
    : [];

  const startTime = roundTime15(new Date(selectedOption.startTime));

  // If the visit is scheduled to start within an hour
  const leaveMinutes = Math.round((startTime.getTime() - (selectedOption.originDrive * 60 * 1000) - Date.now()) / 1000 / 60);

  const shouldDisplayArrivalWindow = new Date(selectedOption.arrivalWindowStart).getTime() !== new Date(selectedOption.arrivalWindowEnd).getTime();

  return (
    <DashboardTile>
      {reschedulingVisit &&
        !isSameDay(reschedulingVisit.startTime, selectedOption.startTime) && (
          <DashboardTileTitle strike>
            {datef(reschedulingVisit.startTime, "iiii, MMMM d")}
          </DashboardTileTitle>
        )}
      <DashboardTileTitle>
        {datef(startTime, "iiii, MMMM d")}
      </DashboardTileTitle>

      {shouldDisplayArrivalWindow ? (
        <>
          <DetailHeader>Arrival Window</DetailHeader>
          {reschedulingVisit &&
            (!isSameMinute(
              reschedulingVisit.arrivalWindowStartTime,
              selectedOption.arrivalWindowStart
            ) ||
              !isSameMinute(
                reschedulingVisit.arrivalWindowEndTime,
                selectedOption.arrivalWindowEnd
              )) && (
              <DetailRow strike>
                {timeRangeLabel(
                  reschedulingVisit.arrivalWindowStartTime,
                  reschedulingVisit.arrivalWindowEndTime,
                  false,
                  true,
                  timezone
                )}
              </DetailRow>
            )}
          <DetailRow>
            {timeRangeLabel(
              selectedOption.arrivalWindowStart,
              selectedOption.arrivalWindowEnd,
              false,
              true,
              timezone
            )}
          </DetailRow>
        </>
      ) : null}

      <DetailHeader>Target Visit Start Time</DetailHeader>
      {reschedulingVisit &&
        !isSameMinute(
          reschedulingVisit.startTime,
          selectedOption.startTime
        ) && (
          <DetailRow strike>
            {timeLabelWithZone(reschedulingVisit.startTime, true, timezone)}
          </DetailRow>
        )}
      <DetailRow>
        {timeLabelWithZone(startTime, true, timezone)}
        {leaveMinutes < 60 && (
          <Tooltip
            title={
              selectedOption.originDrive < 3
                ? `This visit will start in ${leaveMinutes} minutes.`
                : `This visit requires ${providersById[providerIds[0]]?.firstName || "the provider"} to leave in ${leaveMinutes} minutes to arrive on time.`
            }
          >
            <WarningAmberRoundedIcon color="warning" sx={{ ml: 1, verticalAlign: "bottom" }} />
          </Tooltip>
        )}
      </DetailRow>

      <DetailHeader>Visit Duration</DetailHeader>
      {reschedulingVisit && reschedulingVisitDuration !== duration && (
        <DetailRow strike>{reschedulingVisitDuration}mins</DetailRow>
      )}
      <DetailRow>{duration}mins</DetailRow>

      <DetailHeader>Drive Time</DetailHeader>
      <DetailRow>
        {Math.max(selectedOption.originDrive, selectedOption.destinationDrive)}mins
        {!selectedOption.contiguous && <Tooltip title="Leaves a gap between visits, possibly resulting in provider downtime">
          <WarningAmberRoundedIcon color="warning" sx={{ ml: 1, verticalAlign: "bottom" }} />
        </Tooltip>}
        {selectedOption.contiguous && selectedOption.driveForScoring < Math.max(selectedOption.originDrive, selectedOption.destinationDrive) && <Tooltip title={`We scored this visit based on the ${selectedOption.driveForScoring}min drive from the shift center`}>
          <InfoOutlinedIcon color="primary" sx={{ ml: 1, verticalAlign: "bottom" }} />
        </Tooltip>}
      </DetailRow>

      <ProviderSection
        resources={selectedOption.resources}
        reschedulingResources={reschedulingVisit?.visitResources}
        providersById={providersById}
      />

      {serviceNames.length > 0 && (
        <>
          <DetailHeader>Services</DetailHeader>
          {reschedulingVisit &&
            !arrayContentEquals(serviceNames, reschedulingVisitServiceNames) &&
            reschedulingVisitServiceNames.map((name) => (
              <DetailRow key={name} strike>
                {name}
              </DetailRow>
            ))}
          {serviceNames.map((name) => (
            <DetailRow key={name}>{name}</DetailRow>
          ))}
        </>
      )}

      {
      // @ts-ignore
      window.debugMode && (
        <>
          <DetailHeader>Scores</DetailHeader>
          {Object.keys(selectedOption.scores).map((key) => key != '__typename' && (
            <DetailRow key={key}>{key}: {selectedOption.scores[key as keyof typeof selectedOption.scores]}</DetailRow>
          ))}
        </>
      )}
    </DashboardTile>
  );
}

const DetailHeader = ({ children }: PropsWithChildren) => (
  <Typography variant="subtitle1" mt={1.5} mb={-0.2} fontWeight={500}>
    {children}
  </Typography>
);

const DetailRow = ({
  children,
  strike,
}: PropsWithChildren & { strike?: boolean }) => (
  <Typography
    variant="body1"
    color={strike ? "GrayText" : "inherit"}
    sx={{ textDecoration: strike ? "line-through" : "inherit" }}
  >
    {children}
  </Typography>
);

type ProviderSectionProps = {
  resources: SchedulerOption["resources"];
  reschedulingResources?: NonNullable<SchedulerReschedulingVisit>["visitResources"];
  providersById: ProviderMap;
}
const ProviderSection = ({ resources, reschedulingResources, providersById }: ProviderSectionProps) => {
  const sortedResources = [...resources].sort((a, b) => {
    if (a.inHome && !b.inHome) return 1;
    if (!a.inHome && b.inHome) return -1;
    return a.providerId - b.providerId;
  });

  const sortedReschedulingResources = reschedulingResources?.filter((r) => r !== null).sort((a, b) => {
    return a.provider?.id ?? 0 - (b.provider?.id ?? 0);
  }) || [];

  const showProviderChange =
    sortedReschedulingResources.length > 0
      && !arrayContentEquals(
          sortedResources.map((r) => r.providerId),
          sortedReschedulingResources.map((r) => r.provider?.id)
        );

  return (
    <>
      <DetailHeader>
        {sortedResources.length === 1 ? "Provider" : "Providers"}
      </DetailHeader>

      {showProviderChange &&
        sortedReschedulingResources.map((resource) => (
          <DetailRow key={'reschedule-' + resource.provider?.id} strike>
            {providersById[resource.provider?.id ?? 0]?.firstName} {providersById[resource.provider?.id ?? 0]?.lastName}
          </DetailRow>
        ))}

      {sortedResources.map(({providerId, inHome}) => (
        <DetailRow key={providerId}>
          {providersById[providerId]?.firstName} {providersById[providerId]?.lastName} {inHomeOrVirtualLabel(inHome)}
        </DetailRow>
      ))}
    </>
  )
}

const inHomeOrVirtualLabel = (inHome?: boolean) => {
  switch (inHome) {
    case true:
      return "(In patient home)";
    case false:
      return "(Virtual)";
    default:
      return "";
  }
};
